import {Typography} from '@material-ui/core';
import {AsyncPage, isLoaded} from 'Core/models/Async';
import {Page} from 'Core/models/Page';
import * as EnrollmentFileActions from 'Enrollment/ducks/enrollmentFileProcessing';
import {changeEnrollmentFileFilters, getEnrollmentFiles} from 'Enrollment/ducks/enrollmentFileProcessing';
import {ESREnrollmentFile} from 'Enrollment/models/EnrollmentFile';
import {EnrollmentRouteConstants} from 'Enrollment/Routes';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withInequalityFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import {FinalDispositionStatus} from 'Shared/constants/FinalDispositionStatus';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {EnrollmentApplicationStatus} from 'Shared/constants/EnrollmentApplicationStatus';
import InfoIcon from '@material-ui/icons/Info';
import {toFormattedDateAsEst} from 'Shared/utilities/DateFormatter';
import ModularHost from 'Shared/components/ModularHost/ModularHost';
import ToolbarHeader, {ExportToolbarHeaderIconWithCustomError} from 'Shared/components/Toolbar/ToolbarHeader';
import {FileExtension} from 'Shared/components/FileDownloadButton';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';

type ESREnrollmentFilesProps = {
  enrollmentFilesData: AsyncPage<Page<ESREnrollmentFile>>;
};

const ESREnrollmentFilesTable = (props: ESREnrollmentFilesProps) => {
  const dispatch = useDispatch();
  const {enrollmentFilesFilter} = useSelector((state: ReducerState) => state.enrollmentFileProcessing);
  const changeFilter = withValueChangedHook(
    enrollmentFilesFilter as EnrollmentFileActions.ESREnrollmentFileFilters,
    changeEnrollmentFileFilters,
    dispatch
  );
  const filterProperties = {
    changeFilter,
    filterData: enrollmentFilesFilter as EnrollmentFileActions.ESREnrollmentFileFilters,
  };

  const handleEnrollmentFileDetails = () => {
    let enrollmentFilesFilterData = enrollmentFilesFilter as EnrollmentFileActions.ESREnrollmentFileFilters;
    enrollmentFilesFilterData.isEnrollmentFileDetails = true;
    changeEnrollmentFileFilters(enrollmentFilesFilterData);
  };

  return (
    <>
      <div className="enrollment-file-details-header">
        <InfoIcon></InfoIcon>
        <div>
          <Typography component="div">
            <div>Disclaimer: Date showing in Eastern Time Zone</div>
          </Typography>
        </div>
      </div>
      <ModularHost>
        <ToolbarHeader title="Enrollment Files">
          <ExportToolbarHeaderIconWithCustomError
            title="Export Enrollment Files"
            downloadedFileName="Enrollment_Files_Details"
            extension={FileExtension.CSV}
            relativeFileLocation={`/enrollmentFiles/processingHistory/esr/csv${addFilterParameters({
              ...enrollmentFilesFilter,
            })}`}
            totalRecordCount={isLoaded(props.enrollmentFilesData) ? props.enrollmentFilesData.Data.totalCount : 0}
          />
        </ToolbarHeader>
        <PagedView
          pagedViewKey="esrEnrollmentFilesTable"
          data={props.enrollmentFilesData}
          loadMore={page => dispatch(getEnrollmentFiles(page, enrollmentFilesFilter))}
        >
          {columnProps => (
            <>
              <Column
                {...columnProps}
                title="File Name"
                default
                filterProperties={withStringFilterProperties(filterProperties, 'fileName')}
              >
                {enrollmentFile => enrollmentFile.fileName}
              </Column>
              <Column
                {...columnProps}
                title="Account Number"
                filterProperties={withStringFilterProperties(filterProperties, 'accountNumber')}
              >
                {enrollmentFile => enrollmentFile.accountNumber}
              </Column>
              <Column
                {...columnProps}
                title="Technology Partner"
                filterProperties={withStringFilterProperties(filterProperties, 'agencyName')}
              >
                {enrollmentFile => enrollmentFile.agencyName}
              </Column>
              <Column
                {...columnProps}
                title="Load Date"
                default
                filterProperties={withDateFilterProperties(filterProperties, 'createdDate')}
              >
                {enrollmentFile => toFormattedDateAsEst(enrollmentFile.createdDate!.toString())}
              </Column>
              <Column
                {...columnProps}
                title="Total Records"
                default
                filterProperties={withInequalityFilterProperties(filterProperties, 'totalApps')}
              >
                {enrollmentFile =>
                  enrollmentFile.totalApps! > 0 && enrollmentFile.caseRequestID ? (
                    <Link
                      className="enrollment-app-link"
                      to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
                        enrollmentFile.caseRequestID!.toString(),
                        0
                      )}
                    >
                      {enrollmentFile.totalApps}
                    </Link>
                  ) : (
                    <Link
                      className="enrollment-app-link"
                      onClick={() => handleEnrollmentFileDetails()}
                      to={EnrollmentRouteConstants.ENROLLMENT_GENELCOSHEET_STATUS_DETAILS(
                        enrollmentFile.id!.toString()
                      )}
                    >
                      {enrollmentFile.totalApps}
                    </Link>
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="In Progress"
                default
                filterProperties={withInequalityFilterProperties(filterProperties, 'TotalPendingApplications')}
              >
                {enrollmentFile =>
                  enrollmentFile.totalPendingApplications! > 0 && enrollmentFile.caseRequestID ? (
                    <Link
                      className="enrollment-app-link"
                      to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
                        enrollmentFile.caseRequestID!.toString(),
                        FinalDispositionStatus.Pending
                      )}
                    >
                      {enrollmentFile.totalPendingApplications}
                    </Link>
                  ) : (
                    enrollmentFile.totalPendingApplications?.toString()
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Successful"
                default
                filterProperties={withInequalityFilterProperties(filterProperties, 'totalSuccessfulApplications')}
              >
                {enrollmentFile =>
                  enrollmentFile.totalSuccessfulApplications! > 0 && enrollmentFile.caseRequestID ? (
                    <Link
                      className="enrollment-app-link"
                      to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
                        enrollmentFile.caseRequestID!.toString(),
                        FinalDispositionStatus.Complete
                      )}
                    >
                      {enrollmentFile.totalSuccessfulApplications}
                    </Link>
                  ) : (
                    enrollmentFile.totalSuccessfulApplications?.toString()
                  )
                }
              </Column>
              <Column
                {...columnProps}
                title="Unsuccessful"
                default
                filterProperties={withInequalityFilterProperties(filterProperties, 'discrepancyCount')}
              >
                {enrollmentFile =>
                  enrollmentFile.discrepancyCount! > 0 && enrollmentFile.caseRequestID ? (
                    <Link
                      className="enrollment-app-link"
                      to={EnrollmentRouteConstants.ENROLLMENT_FILE_APPLICATION_STATUS_DETAILS(
                        enrollmentFile.caseRequestID!.toString(),
                        0,
                        EnrollmentApplicationStatus.Discrepancy
                      )}
                    >
                      {enrollmentFile.discrepancyCount}
                    </Link>
                  ) : (
                    enrollmentFile.discrepancyCount
                  )
                }
              </Column>
              <Column {...columnProps} title="Days to Process" default>
                {enrollmentFile =>
                  enrollmentFile.caseRequestID == null ? 'N/A' : enrollmentFile.numberOfDaysToProcess
                }
              </Column>

              <Column {...columnProps} title="Days to Complete" default>
                {enrollmentFile =>
                  enrollmentFile.numberOfDaysToComplete == null ? 'In Progress' : enrollmentFile.numberOfDaysToComplete
                }
              </Column>
            </>
          )}
        </PagedView>
      </ModularHost>
    </>
  );
};

export default ESREnrollmentFilesTable;
